import React, { useState } from 'react';
import { View } from '../Common/react-native-components';
import { Typography, Button, Popup } from '@hmsexp/component-library';
import styles from './styles';
import {
  DOWNLOAD_FILTER_KEYS,
  GENERATE_REPORT_TITLE,
  GENERATING_REPORT_PREFIX,
  WITH_FILTERS_SUFFIX,
  NO_FILTERS_APPLIED,
  CANCEL,
  CONFIRM,
  EMAIL_LABEL,
  EMAIL_PLACEHOLDER,
  EMAIL_REGEX
} from './downloadConstants';

const ConfirmReportPopup = ({
  isOpen,
  onClose,
  onConfirm,
  selectedReport,
  appliedFilters,
  email,
  setEmail
}) => {
  const [emailTouched, setEmailTouched] = useState(false);
  const isEmailValid = EMAIL_REGEX.test(email || '');
  const showEmailError = emailTouched && !isEmailValid;

  React.useEffect(() => {
    if (isOpen) setEmailTouched(false);
  }, [isOpen]);

  const handleClose = () => {
    setEmailTouched(false);
    onClose();
  };

  const getFilterInfo = (key, value) => {
    const lowerKey = key.toLowerCase();
    let labels = '';

    if (lowerKey === 'timeperiod') {
      const tp = value?.[0];
      labels = tp?.label || (tp?.start_date && tp?.end_date ? `${tp.start_date} to ${tp.end_date}` : '');
    } else if (Array.isArray(value)) {
      labels = value.flat().map(item => item?.fullLabel || item?.label || item).filter(Boolean).join(', ');
    }

    if (!labels || labels.trim() === '') return null;

    const cleanKey = key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

    return { key, label: cleanKey, value: labels };
  };

  return (
    <Popup
      visible={isOpen}
      position="center"
      showHeader
      showFooter={false}
      showCloseIcon={false}
      isScrollableContent={false}
      onRequestClose={handleClose}
      renderHeader={() => (
        <View style={styles.endPopupHeader}>
          <Typography variant="h4" fontWeightType="semiBold">
            {GENERATE_REPORT_TITLE}
          </Typography>
        </View>
      )}
      renderContent={() => (
        <View style={styles.endPopupContent}>
          <Typography
            variant="body"
            textProps={{ style: styles.endPopupDescription }}
            fontWeightType="regular"
          >
            {GENERATING_REPORT_PREFIX} {selectedReport?.report_name}({selectedReport?.report_type}) {WITH_FILTERS_SUFFIX}
          </Typography>

          <View style={styles.filterList}>
            {appliedFilters && Object.keys(appliedFilters).length > 0 ? (
              (() => {
                const orderedItems = DOWNLOAD_FILTER_KEYS.reduce((acc, key) => {
                  const matchingEntry = Object.entries(appliedFilters).find(
                    ([k]) => k.toLowerCase() === key.toLowerCase()
                  );
                  if (!matchingEntry) return acc;

                  const info = getFilterInfo(matchingEntry[0], matchingEntry[1]);
                  if (info) acc.push(info);
                  return acc;
                }, []);

                if (selectedReport?.api_report_type === 'ALERTS') {
                  orderedItems.push({
                    key: 'alert_type',
                    label: 'Alert Type',
                    value: 'All alerts'
                  });
                }

                if (orderedItems.length === 0) {
                  return (
                    <Typography
                      variant="body"
                      textProps={{ style: styles.endPopupDescription }}
                      fontWeightType="regular"
                    >
                      {NO_FILTERS_APPLIED}
                    </Typography>
                  );
                }

                return (
                  <>
                    {orderedItems.map((item) => (
                      <View key={item.key} style={styles.filterItem}>
                        <Typography
                          variant="body"
                          textProps={{ style: styles.endPopupDescription }}
                          fontWeightType="regular"
                        >
                          {item.label}: {item.value}
                        </Typography>
                      </View>
                    ))}
                  </>
                );
              })()
            ) : (
              <Typography
                variant="body"
                textProps={{ style: styles.endPopupDescription }}
                fontWeightType="regular"
              >
                {NO_FILTERS_APPLIED}
              </Typography>
            )}
          </View>

          <View style={styles.emailInputContainer}>
            <Typography
              variant="body"
              fontWeightType="semiBold"
              textProps={{ style: styles.emailLabel }}
            >
              {EMAIL_LABEL}
            </Typography>
            <input
              type="email"
              value={email || ''}
              placeholder={EMAIL_PLACEHOLDER}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setEmailTouched(true)}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: 14,
                border: showEmailError ? '1px solid #e53935' : '1px solid #ccc',
                borderRadius: 6,
                outline: 'none',
                boxSizing: 'border-box',
                marginTop: 4,
                minHeight: 44
              }}
            />
            {showEmailError && (
              <Typography
                variant="caption"
                textProps={{ style: { color: '#e53935', marginTop: 4 } }}
              >
                Please enter a valid email address.
              </Typography>
            )}
          </View>

          <View style={styles.endPopupButtonsRow}>
            <Button
              title={CANCEL}
              onClick={handleClose}
              style={{
                buttonContainer: styles.endCancelButton,
                buttonText: styles.endCancelButtonText
              }}
            />
            <Button
              title={CONFIRM}
              onClick={onConfirm}
              disabled={!isEmailValid}
              style={{
                buttonContainer: [
                  styles.endConfirmButton,
                  !isEmailValid && { opacity: 0.4 }
                ],
                buttonText: styles.endConfirmButtonText
              }}
            />
          </View>
        </View>
      )}
      style={{
        contentPosition: styles.endPopupContentPosition,
        popupBody: styles.endPopupBody
      }}
    />
  );
};

export default ConfirmReportPopup;